import { useState } from 'react';
import { captureForms, formResponses, lcKPIs } from '../../mocks/leadCapture';
import type { CaptureForm, FormResponse } from '../../mocks/leadCapture';
import FormCard from './components/FormCard';
import FormBuilder from './components/FormBuilder';
import EmbedCodeModal from './components/EmbedCodeModal';
import ResponsesInbox from './components/ResponsesInbox';
import ConversionAnalytics from './components/ConversionAnalytics';

type Tab = 'forms' | 'responses' | 'analytics';

export default function LeadCapturePage() {
  const [activeTab, setActiveTab] = useState<Tab>('forms');
  const [forms, setForms] = useState<CaptureForm[]>(captureForms);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<CaptureForm | null>(null);
  const [embedForm, setEmbedForm] = useState<CaptureForm | null>(null);
  const [responseFormFilter, setResponseFormFilter] = useState<string | null>(null);
  const [convertedBanner, setConvertedBanner] = useState<string | null>(null);

  const newCounts: Record<string, number> = {};
  forms.forEach((f) => {
    newCounts[f.id] = formResponses.filter((r) => r.formId === f.id && r.status === 'New').length;
  });
  const totalNew = Object.values(newCounts).reduce((a, b) => a + b, 0);

  const handleSaveForm = (form: CaptureForm) => {
    setForms((prev) => {
      const exists = prev.find((f) => f.id === form.id);
      if (exists) return prev.map((f) => (f.id === form.id ? form : f));
      return [...prev, form];
    });
    setBuilderOpen(false);
    setEditingForm(null);
  };

  const handleToggleStatus = (formId: string) => {
    setForms((prev) =>
      prev.map((f) => {
        if (f.id !== formId) return f;
        const next = f.status === 'Active' ? 'Inactive' : 'Active';
        return { ...f, status: next };
      })
    );
  };

  const handleViewResponses = (form: CaptureForm) => {
    setResponseFormFilter(form.id);
    setActiveTab('responses');
  };

  const handleConvertToLead = (response: FormResponse) => {
    const name = Object.values(response.data)[0] || 'New Lead';
    setConvertedBanner(name);
    setTimeout(() => setConvertedBanner(null), 4000);
  };

  const kpiCards = [
    {
      label: 'Active Forms',
      value: `${forms.filter((f) => f.status === 'Active').length} / ${forms.length}`,
      sub: `${forms.filter((f) => f.status === 'Draft').length} drafts pending`,
      icon: 'ri-layout-2-line',
      color: '#0ea5e9',
    },
    {
      label: 'Total Submissions',
      value: lcKPIs.totalSubmissions.toLocaleString(),
      sub: `${lcKPIs.submissionsThisMonth} this month`,
      icon: 'ri-send-plane-line',
      color: '#6366f1',
    },
    {
      label: 'Leads Converted',
      value: lcKPIs.totalConverted.toString(),
      sub: `${lcKPIs.conversionRate}% conversion rate`,
      icon: 'ri-user-add-line',
      color: '#10b981',
    },
    {
      label: 'New Responses',
      value: totalNew.toString(),
      sub: 'Waiting for review',
      icon: 'ri-inbox-line',
      color: '#f59e0b',
    },
  ];

  return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#f9fafb]">
      {/* Success banner */}
      {convertedBanner && (
        <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-white border border-emerald-200 text-gray-800 text-sm px-4 py-3 rounded-xl shadow-md">
          <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-follow-line text-emerald-500" />
          </div>
          <span><strong>{convertedBanner}</strong> has been converted to a lead!</span>
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-sky-50">
            <i className="ri-user-received-2-line text-xl text-sky-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Lead Capture Forms</h1>
            <p className="text-sm text-gray-400 mt-0.5">Build embeddable forms, capture enquiries, and convert them into leads</p>
          </div>
        </div>
        <button
          onClick={() => { setEditingForm(null); setBuilderOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-700 transition-colors"
        >
          <i className="ri-add-line" />
          New Form
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpiCards.map((k) => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-gray-500">{k.label}</p>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${k.color}14` }}>
                <i className={`${k.icon} text-base`} style={{ color: k.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800 leading-none">{k.value}</p>
            <p className="text-xs text-gray-400 mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-6">
        {([
          { key: 'forms', label: 'My Forms', icon: 'ri-layout-grid-line' },
          { key: 'responses', label: 'Responses', icon: 'ri-inbox-line' },
          { key: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-2-line' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); if (t.key !== 'responses') setResponseFormFilter(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === t.key ? 'bg-white text-gray-800' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${t.icon} text-base`} />
            {t.label}
            {t.key === 'responses' && totalNew > 0 && (
              <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium">{totalNew}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab: Forms */}
      {activeTab === 'forms' && (
        <div>
          {/* Status filter bar */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            {['All', 'Active', 'Inactive', 'Draft'].map((s) => {
              const count = s === 'All' ? forms.length : forms.filter((f) => f.status === s).length;
              return (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-500">
                  {s} ({count})
                </span>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((form) => (
              <FormCard
                key={form.id}
                form={form}
                responsesCount={formResponses.filter((r) => r.formId === form.id).length}
                newCount={newCounts[form.id] || 0}
                onEdit={(f) => { setEditingForm(f); setBuilderOpen(true); }}
                onEmbed={(f) => setEmbedForm(f)}
                onToggleStatus={handleToggleStatus}
                onViewResponses={handleViewResponses}
              />
            ))}

            {/* Add new form card */}
            <button
              onClick={() => { setEditingForm(null); setBuilderOpen(true); }}
              className="bg-white rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all group min-h-[240px]"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                <i className="ri-add-line text-2xl text-gray-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Create New Form</p>
                <p className="text-xs text-gray-400 mt-0.5">Build a custom lead capture form</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Tab: Responses */}
      {activeTab === 'responses' && (
        <ResponsesInbox
          forms={forms}
          filterFormId={responseFormFilter}
          onConvertToLead={handleConvertToLead}
        />
      )}

      {/* Tab: Analytics */}
      {activeTab === 'analytics' && <ConversionAnalytics />}

      {/* Form Builder Modal */}
      {builderOpen && (
        <FormBuilder
          form={editingForm}
          onClose={() => { setBuilderOpen(false); setEditingForm(null); }}
          onSave={handleSaveForm}
        />
      )}

      {/* Embed Code Modal */}
      {embedForm && (
        <EmbedCodeModal
          form={embedForm}
          onClose={() => setEmbedForm(null)}
        />
      )}
    </div>
  );
}
